import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTasks } from '@dev-streetbeat/chat/react';
import type { TaskDetails } from '@dev-streetbeat/chat/types';

import { ChatAgentCheckIcon } from 'shared/assets/svg/ChatAgentCheckIcon';

import styles from './taskStatuses.module.scss';

type Props = {};

export const TaskStatuses: React.FC<Props> = () => {
  const { tasks } = useTasks();
  // Use tasks from useTasks hook if available, otherwise fall back to parsing message content
  const tasksToRender = useMemo(() => {
    const taskList = tasks.length > 0 ? tasks : ([] as TaskDetails[]);
    return taskList.filter((task) => {
      const statusText = task.message || task.title || task.description;

      return (
        statusText === 'Pre-elaborazione.' ||
        statusText === 'Preprocessing.' ||
        statusText === 'Fineco Create Portfolio' ||
        statusText === 'Fineco Set Constraints'
      );
    });
  }, [tasks]);

  // Fallback: when no task in the allow-list matches (e.g. during the gap
  // between task.completed and the first assistant message.started, or while
  // an off-allow-list sub-task is running), keep a continuous "refining"
  // message visible. The outer skeleton in MessagesList already gates this
  // component on `runInProgress && messages[last].role === USER && !streamingMessage`,
  // so the fallback only renders during the right phase of a run.
  if (!tasksToRender || tasksToRender.length === 0) {
    return (
      <div className={styles.taskStatusesContainer}>
        <div className={styles.taskStatusItem}>
          <div className={styles.taskStatusContent}>
            <div className={styles.checkIconContainer}>
              <ChatAgentCheckIcon />
            </div>
            <span className={styles.taskStatusText}>
              Sto affinando l’analisi per garantire informazioni aggiornate e accurate.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.taskStatusesContainer}>
      {tasksToRender.map((task, index) => (
        <TaskStatusItem key={task.task_id || index} task={task as TaskDetails} />
      ))}
    </div>
  );
};

const TaskStatusItem: React.FC<{ task: TaskDetails }> = ({ task }) => {
  const { t } = useTranslation();

  const getStatusText = () => {
    let statusText = task.message || task.title || task.description || t('assistant.status.processing');

    if (statusText === 'Pre-elaborazione.' || statusText === 'Preprocessing.') {
      return 'Sto affinando l’analisi per garantire informazioni aggiornate e accurate.';
    } else if (statusText === 'Fineco Create Portfolio') {
      if (!task.is_completed) {
        return 'Sto generando il portafoglio ottimizzato.';
      } else {
        return 'Il portafoglio è stato creato con successo.';
      }
    } else if (statusText === 'Fineco Set Constraints') {
      if (!task.is_completed) {
        statusText = 'I vincoli sono stati impostati correttamente.';
      } else {
        statusText = 'I vincoli sono stati definiti con successo.';
      }
    }

    // NOTE: When we decide to include task progress,
    // uncomment this (above code should be commented and italian text should be coming from backend in this case)

    // if (!task.is_completed) {
    //   // Add progress if available
    //   if (task.progress && task.progress > 0) {
    //     const progressPercent = Math.round(task.progress * 100);
    //     statusText += ` (${progressPercent}%)`;
    //   }

    //   // Add step information
    //   if (task.current_step && task.total_steps) {
    //     statusText += ` - ${t('assistant.tasks.step')} ${task.current_step}/${task.total_steps}`;
    //   }
    // }

    return statusText;
  };

  return (
    <div className={styles.taskStatusItem}>
      <div className={styles.taskStatusContent}>
        <div className={styles.checkIconContainer}>
          <ChatAgentCheckIcon />
        </div>

        <span className={styles.taskStatusText}>{getStatusText()}</span>
      </div>
      {/* {!task.is_completed && task.progress != null && (
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${Math.round(task.progress * 100)}%` }} />
        </div>
      )} */}
    </div>
  );
};

export default TaskStatuses;
